import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { dispatch } from '@/redux';
import { getUser } from '@/redux/reducers';
import { BhabiStorage } from '@/shared/lib/storage';
import { GuestShell, LoadingState } from '../components/guest-shell';
import { NameForm } from '../components/name-form';
import {
  apiErrorMessage,
  applyToken,
  fetchInvitation,
  joinTable,
  leaveTable,
  startGuestSession,
  useSessionInfo,
} from '../session';
import {
  Invitation,
  invitationProblem,
  parseRoomInput,
  PROBLEM_COPY,
  shortTableCode,
} from '../lib/invite';
import { GUEST_ROUTES } from '../routes.definitions';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'network' }
  | { kind: 'ready'; invitation: Invitation };

const ProblemCard: FC<{
  title: string;
  body: string;
  children?: React.ReactNode;
}> = ({ title, body, children }) => (
  <div className="st-card st-stack" data-suit="hearts" role="alert">
    <h1 className="st-h2">{title}</h1>
    <p className="st-muted">{body}</p>
    {children}
    <Link
      to={GUEST_ROUTES.NEW_TABLE}
      className="st-btn st-btn--primary st-btn--block"
    >
      Start my own table
    </Link>
    <Link to="/" className="st-btn st-btn--on-ivory st-btn--block">
      Back home
    </Link>
  </div>
);

export const JoinPage: FC = () => {
  const params = useParams<{ roomID: string }>();
  const roomID = parseRoomInput(params.roomID || '');
  const nav = useNavigate();
  const {
    isAccount,
    isGuest,
    hasSession,
    name,
    token,
    roomID: seatedRoomID,
  } = useSessionInfo();

  const [state, setState] = useState<LoadState>(
    roomID ? { kind: 'loading' } : { kind: 'invalid' }
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);

  const load = useCallback(async () => {
    if (!roomID) return;
    setState({ kind: 'loading' });
    try {
      const invitation = await fetchInvitation(roomID);
      setState(
        invitation ? { kind: 'ready', invitation } : { kind: 'invalid' }
      );
    } catch {
      // offline, or the server failed: either way the link may still be fine
      setState({ kind: 'network' });
    }
  }, [roomID]);

  useEffect(() => {
    load();
  }, [load]);

  const isMember = !!roomID && seatedRoomID === roomID;
  const tablePath = isAccount ? '/in-play' : GUEST_ROUTES.TABLE;

  /** Take a seat with the given (lobby or other-room) token. */
  const sitDown = async (activeToken: string) => {
    if (!roomID) return;
    let lobbyToken = activeToken;
    // Guests can only sit at one table: leave the old one first.
    if (seatedRoomID && seatedRoomID !== roomID && !isAccount) {
      lobbyToken = await leaveTable(activeToken);
      await applyToken(lobbyToken);
    }
    const roomToken = await joinTable(lobbyToken, roomID);
    if (isAccount) {
      // same path as the account "pending rooms" rejoin
      await BhabiStorage.setToken(roomToken);
      dispatch(getUser(roomToken));
    } else {
      await applyToken(roomToken);
    }
    nav(tablePath, { replace: true });
  };

  const attempt = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't take a seat. Try again."));
      setBusy(false);
      // the table may have filled or started meanwhile
      load();
    }
  };

  const onNameAndJoin = (newName: string) =>
    attempt(async () => {
      const { token: guestToken } = await startGuestSession(newName);
      setEditingName(false);
      await sitDown(guestToken);
    });

  const onJoin = () =>
    attempt(async () => {
      if (token) await sitDown(token);
    });

  // Already seated here with a live token: straight back to the table.
  if (isMember && state.kind === 'ready') {
    return <Navigate to={tablePath} replace />;
  }

  const content = (() => {
    if (state.kind === 'invalid') {
      return (
        <ProblemCard
          title="That invite link doesn't open a table"
          body="It may be mistyped or cut off. Ask your friend to send the link again, or start your own table."
        />
      );
    }
    if (state.kind === 'loading') {
      return <LoadingState label="Finding the table…" />;
    }
    if (state.kind === 'network') {
      return (
        <div className="st-card st-stack" data-suit="spades" role="alert">
          <h1 className="st-h2">We can&apos;t reach the table</h1>
          <p className="st-muted">
            Check your connection. The invite is still good; nothing has been
            lost.
          </p>
          <button
            type="button"
            className="st-btn st-btn--primary st-btn--block"
            onClick={load}
          >
            Try again
          </button>
        </div>
      );
    }

    const inv = state.invitation;
    const problem = invitationProblem(inv, { isMember });
    if (problem) {
      const copy = PROBLEM_COPY[problem];
      return (
        <ProblemCard title={copy.title} body={copy.body}>
          {problem === 'in-progress' && hasSession && (
            <button
              type="button"
              className="st-btn st-btn--on-ivory st-btn--block"
              onClick={onJoin}
              disabled={busy}
            >
              {busy ? 'Checking your seat…' : 'I was playing here: rejoin'}
            </button>
          )}
          {error && (
            <p className="st-error" role="alert">
              {error}
            </p>
          )}
        </ProblemCard>
      );
    }

    const open = inv.numberOfPlayers - inv.joinedPlayers;
    const seatedElsewhere = !!seatedRoomID && seatedRoomID !== roomID;

    return (
      <div className="st-stack" style={{ ['--st-gap' as string]: '20px' }}>
        <section className="st-stack">
          <p className="st-eyebrow">
            You&apos;re invited · Table {shortTableCode(inv.roomID)}
          </p>
          <h1
            className="st-display"
            style={{ fontSize: 'clamp(2.3rem, 9vw, 3.6rem)' }}
          >
            A seat is <em>waiting.</em>
          </h1>
          <p className="st-lead">
            {inv.joinedPlayers} of {inv.numberOfPlayers} players seated, {open}{' '}
            open seat{open === 1 ? '' : 's'}. Cards are dealt as soon as the
            table is full.
          </p>
        </section>

        <div className="st-card st-stack" data-suit="spades">
          {!hasSession || editingName ? (
            <NameForm
              defaultName={name}
              submitLabel="Take my seat"
              busyLabel="Sitting down…"
              onSubmit={onNameAndJoin}
              serverError={error}
            />
          ) : (
            <>
              {seatedElsewhere && (
                <p className="st-banner st-banner--info st-small" role="note">
                  {isAccount
                    ? 'You are seated at another table. Leave it from that table first, then come back to this link.'
                    : "You're seated at another table. Joining this one will leave that table."}
                </p>
              )}
              <button
                type="button"
                className="st-btn st-btn--primary st-btn--block"
                onClick={onJoin}
                disabled={busy || (seatedElsewhere && isAccount)}
                aria-busy={busy}
              >
                {busy
                  ? 'Sitting down…'
                  : seatedElsewhere
                    ? `Leave other table & join as ${name}`
                    : `Join as ${name}`}
              </button>
              {error && (
                <p className="st-error" role="alert">
                  {error}
                </p>
              )}
              {isGuest && (
                <p className="st-small st-muted">
                  Not {name}?{' '}
                  <button
                    type="button"
                    className="st-link"
                    onClick={() => setEditingName(true)}
                  >
                    Use a different name
                  </button>
                </p>
              )}
            </>
          )}
        </div>

        <Link to="/game-rules" className="st-link st-small">First time? Learn the rules</Link>

        {!hasSession && (
          <p className="st-small st-muted">
            Have an account?{' '}
            <Link
              className="st-link"
              to={`/sign-in?next=${encodeURIComponent(`/join/${roomID}`)}`}
            >
              Sign in
            </Link>{' '}
            and come straight back to this table.
          </p>
        )}
      </div>
    );
  })();

  return <GuestShell title="Join a Bhabi table">{content}</GuestShell>;
};
